/**
 * ProjectionsTile Component
 *
 * Displays the monthly projection summary for the selected location/department/month.
 * Shows: Units (for vehicle departments), Gross, Expenses, and Profit.
 * Data comes directly from backend CALCULATED records.
 *
 * Supports multiple display modes:
 * - card: Matches gross comparison cards style (default)
 * - compact: Small inline display with unit tiles
 * - full: Full-width horizontal layout
 */

import React from 'react';
import { ProjectionTargetData } from '../../types/forecast';
import { DepartmentKey } from './DepartmentTabs';

interface ProjectionsTileProps {
  /** Current month (1-12) */
  month: number;
  /** Current year */
  year: number;
  /** Selected department */
  department: DepartmentKey;
  /** Projection data from API */
  projectionData: ProjectionTargetData;
  /** Display mode: 'card' (matches gross cards), 'compact', or 'full' */
  mode?: 'card' | 'compact' | 'full';
  /** Hide expense and profit values (e.g., for MarketingManager) */
  hideExpenseProfit?: boolean;
  /** @deprecated Use mode='compact' instead */
  compact?: boolean;
}

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const formatCurrency = (value: number | null | undefined, abbreviated?: boolean): string => {
  if (value === null || value === undefined) return '—';
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // In abbreviated mode, use K/M suffixes
  // Use K for values under $10M to preserve precision (e.g., $5,647K not $5.6M)
  if (abbreviated && absValue >= 1000) {
    const formatted = absValue >= 10000000
      ? `$${(absValue / 1000000).toFixed(1)}M`
      : `$${Math.round(absValue / 1000).toLocaleString()}K`;
    return isNegative ? `(${formatted})` : formatted;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(absValue);
  return isNegative ? `(${formatted})` : formatted;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

export const ProjectionsTile: React.FC<ProjectionsTileProps> = ({
  month,
  year,
  department,
  projectionData,
  mode,
  hideExpenseProfit = false,
  compact = false,
}) => {
  const monthName = MONTH_NAMES_SHORT[month - 1] || '';
  const showUnits = department === 'ALL' || department === 'NEW_VEHICLE' || department === 'USED_VEHICLE';
  // Always calculate profit from gross - expenses for consistency
  const calculatedProfit = projectionData.totalExpenses != null
    ? projectionData.monthlyProjection - projectionData.totalExpenses
    : projectionData.operatingProfit ?? null;
  const isNegativeProfit = (calculatedProfit ?? 0) < 0;

  // Determine display mode (support legacy compact prop)
  const displayMode = mode || (compact ? 'compact' : 'card');

  // Card mode: matches gross comparison cards style
  if (displayMode === 'card') {
    return (
      <div className="rounded-lg shadow-sm border p-4 bg-purple-50 border-purple-200">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-600">{monthName} Projection</h4>
            <p className="text-xs text-gray-400 mt-0.5">Target gross for month</p>
            {department === 'ALL' && (
              <p className="text-xs text-gray-400">Includes Others</p>
            )}
          </div>
          {/* Expenses & Profit indicators (hidden for MarketingManager) */}
          {!hideExpenseProfit && (
            <div className="text-right space-y-0.5">
              <div className="text-gray-600">
                <span className="text-xs">Exp: </span>
                <span className="text-xs font-medium">{formatCurrency(projectionData.totalExpenses, true)}</span>
              </div>
              <div className={isNegativeProfit ? 'text-red-600' : 'text-green-600'}>
                <span className="text-xs">Profit: </span>
                <span className="text-xs font-medium">{formatCurrency(calculatedProfit, true)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="mt-2 text-2xl font-bold text-purple-700">
          {formatCurrency(projectionData.monthlyProjection)}
        </div>

        {/* Units for vehicle departments */}
        {showUnits && projectionData.units && (
          <p className="mt-1 text-xs text-gray-500">
            Target: {formatNumber(projectionData.units)} units
          </p>
        )}
      </div>
    );
  }

  // Compact mode: small inline display
  if (displayMode === 'compact') {
    return (
      <div className="bg-gray-50 rounded-lg p-3 flex flex-col h-full min-w-[140px]">
        <h3 className="text-xs font-semibold text-gray-600 mb-2 text-center">
          {monthName} Projected
        </h3>

        <div className="flex-1 space-y-1">
          {showUnits && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Units</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatNumber(projectionData.units)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Gross</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(projectionData.monthlyProjection, true)}
            </span>
          </div>

          {!hideExpenseProfit && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Exp</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(projectionData.totalExpenses, true)}
                </span>
              </div>

              <div className="border-t border-gray-300 my-1" />

              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-gray-600">Profit</span>
                <span className={`text-sm font-bold ${isNegativeProfit ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(calculatedProfit, true)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Full mode: full-width horizontal layout
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        {monthName} {year} Projections
      </h3>

      <div className="flex flex-wrap gap-6">
        <div className="flex flex-col">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Gross</span>
          <span className="text-xl font-semibold text-gray-900">
            {formatCurrency(projectionData.monthlyProjection)}
          </span>
        </div>

        {!hideExpenseProfit && (
          <>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Expenses</span>
              <span className="text-xl font-semibold text-gray-900">
                {formatCurrency(projectionData.totalExpenses)}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Profit</span>
              <span className={`text-xl font-bold ${isNegativeProfit ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(calculatedProfit)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
